'use client'

import { ChevronLeftIcon } from '@heroicons/react/20/solid'
import clsx from 'clsx'
import {
  AnimatePresence,
  motion,
  useIsPresent,
  useReducedMotion,
} from 'framer-motion'
import {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  softEditorialEase,
  softMotionDuration,
} from '@/shared/components/motion/motion-tokens'

import styles from './DatePickerCalendar.module.css'

const YEAR_COLUMN_COUNT = 4
const MONTH_COLUMN_COUNT = 3
const DAY_COLUMN_COUNT = 7
const YEAR_ROW_HEIGHT = 48
const YEAR_EDGE_PEEK = 20
const YEAR_SELECTED_ROW_OFFSET = YEAR_ROW_HEIGHT * 2 - YEAR_EDGE_PEEK

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const

const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'] as const

export type DatePickerCalendarView = 'year' | 'month' | 'day'

export type DatePickerCalendarValue = {
  year: number
  month: number
  day: number
}

type DatePickerCalendarDraft = Partial<DatePickerCalendarValue>
type TransitionDirection = 'forward' | 'back'

export type DatePickerCalendarProps = {
  value?: DatePickerCalendarValue
  defaultValue?: DatePickerCalendarDraft
  selectedYear?: number
  startYear?: number
  endYear?: number
  initialView?: DatePickerCalendarView
  onChange?: (value: DatePickerCalendarValue) => void
  onYearSelect?: (year: number) => void
  onMonthSelect?: (month: number) => void
  onViewChange?: (view: DatePickerCalendarView) => void
  title?: string
  scrollHint?: string
  className?: string
}

type CalendarCell = {
  date: Date
  day: number
  isCurrentMonth: boolean
}

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month, 0).getDate()
}

function clampDay(year: number, month: number, day?: number) {
  if (day === undefined) return undefined
  return Math.min(day, getDaysInMonth(year, month))
}

function getInitialView(
  initialView: DatePickerCalendarView,
  draft: DatePickerCalendarDraft,
) {
  if (initialView === 'day' && (!draft.year || !draft.month)) {
    return draft.year ? 'month' : 'year'
  }

  if (initialView === 'month' && !draft.year) return 'year'

  return initialView
}

function getCalendarCells(year: number, month: number): CalendarCell[] {
  const firstDay = new Date(year, month - 1, 1)
  const mondayFirstOffset = (firstDay.getDay() + 6) % 7
  const daysInMonth = getDaysInMonth(year, month)
  const cellCount = Math.ceil((mondayFirstOffset + daysInMonth) / 7) * 7

  return Array.from({ length: cellCount }, (_, index) => {
    const date = new Date(year, month - 1, index - mondayFirstOffset + 1)

    return {
      date,
      day: date.getDate(),
      isCurrentMonth: date.getMonth() === month - 1,
    }
  })
}

const pickerViewVariants = {
  hidden: (direction: TransitionDirection) => ({
    opacity: 0,
    y: direction === 'forward' ? 12 : -12,
  }),
  visible: {
    opacity: [0, 0, 1],
    y: 0,
    transition: {
      opacity: {
        duration: softMotionDuration.handoff,
        ease: softEditorialEase,
        times: [0, 0.18, 1],
      },
      y: {
        duration: softMotionDuration.handoff,
        ease: softEditorialEase,
      },
    },
  },
  exit: (direction: TransitionDirection) => ({
    opacity: [1, 0, 0],
    y: direction === 'forward' ? -8 : 8,
    transition: {
      opacity: {
        duration: softMotionDuration.handoff,
        ease: softEditorialEase,
        times: [0, 0.38, 1],
      },
      y: {
        duration: softMotionDuration.handoff,
        ease: softEditorialEase,
      },
    },
  }),
}

function AnimatedPickerView({
  children,
  direction,
  reduceMotion,
}: {
  children: ReactNode
  direction: TransitionDirection
  reduceMotion: boolean
}) {
  const isPresent = useIsPresent()

  return (
    <motion.div
      aria-hidden={isPresent ? undefined : true}
      className={styles.view}
      custom={direction}
      data-picker-view-layer=""
      data-present={isPresent}
      initial={reduceMotion ? false : 'hidden'}
      animate="visible"
      exit={reduceMotion ? undefined : 'exit'}
      inert={isPresent ? undefined : true}
      variants={pickerViewVariants}
    >
      {children}
    </motion.div>
  )
}

export default function DatePickerCalendar({
  value,
  defaultValue,
  selectedYear,
  startYear = 1900,
  endYear = new Date().getFullYear(),
  initialView = 'year',
  onChange,
  onYearSelect,
  onMonthSelect,
  onViewChange,
  title = 'Year you were born',
  scrollHint = 'Scroll for more years',
  className,
}: DatePickerCalendarProps) {
  const initialDraft = value ?? defaultValue ?? { year: selectedYear }
  const [draft, setDraft] = useState<DatePickerCalendarDraft>(initialDraft)
  const [view, setView] = useState<DatePickerCalendarView>(() =>
    getInitialView(initialView, initialDraft),
  )
  const [direction, setDirection] = useState<TransitionDirection>('forward')
  const prefersReducedMotion = useReducedMotion()
  const calendarId = useId()
  const headingId = `${calendarId}-${view}-heading`
  const hintId = `${calendarId}-hint`
  const yearListRef = useRef<HTMLDivElement>(null)
  const yearButtonRefs = useRef<Array<HTMLButtonElement | null>>([])
  const monthButtonRefs = useRef<Array<HTMLButtonElement | null>>([])
  const dayButtonRefs = useRef<Array<HTMLButtonElement | null>>([])
  const previousSelectedYear = useRef<number | undefined>(undefined)
  const hasMounted = useRef(false)
  const shouldFocusNextView = useRef(false)

  const years = useMemo(() => {
    if (endYear < startYear) return []

    return Array.from(
      { length: endYear - startYear + 1 },
      (_, index) => startYear + index,
    )
  }, [endYear, startYear])

  const calendarCells = useMemo(() => {
    if (!draft.year || !draft.month) return []
    return getCalendarCells(draft.year, draft.month)
  }, [draft.month, draft.year])

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const dayGridHeight =
    (calendarCells.length / DAY_COLUMN_COUNT) * 36 +
    Math.max(calendarCells.length / DAY_COLUMN_COUNT - 1, 0) * 4
  const panelHeight =
    view === 'year' ? 342 : view === 'month' ? 288 : 116 + dayGridHeight

  useEffect(() => {
    if (value) {
      setDraft(value)
      return
    }

    if (selectedYear !== undefined) {
      setDraft((currentDraft) => ({
        ...currentDraft,
        year: selectedYear,
      }))
    }
  }, [selectedYear, value])

  useEffect(() => {
    const yearList = yearListRef.current
    const selectedIndex = years.indexOf(draft.year ?? -1)

    if (!yearList || selectedIndex < 0) return

    const selectedRowTop =
      Math.floor(selectedIndex / YEAR_COLUMN_COUNT) * YEAR_ROW_HEIGHT

    if (previousSelectedYear.current === undefined) {
      yearList.scrollTop = Math.max(
        0,
        selectedRowTop - YEAR_SELECTED_ROW_OFFSET,
      )
    } else if (selectedRowTop < yearList.scrollTop) {
      yearList.scrollTop = selectedRowTop
    } else if (
      selectedRowTop + YEAR_ROW_HEIGHT >
      yearList.scrollTop + yearList.clientHeight
    ) {
      yearList.scrollTop =
        selectedRowTop + YEAR_ROW_HEIGHT - yearList.clientHeight
    }

    previousSelectedYear.current = draft.year
  }, [draft.year, years, view])

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true
      return
    }

    if (!shouldFocusNextView.current) return
    shouldFocusNextView.current = false

    const animationFrame = window.requestAnimationFrame(() => {
      if (view === 'year') {
        const selectedIndex = years.indexOf(draft.year ?? -1)
        yearButtonRefs.current[Math.max(selectedIndex, 0)]?.focus()
      } else if (view === 'month') {
        monthButtonRefs.current[Math.max((draft.month ?? 1) - 1, 0)]?.focus()
      } else {
        const selectedCellIndex = calendarCells.findIndex(
          (cell) => cell.isCurrentMonth && cell.day === (draft.day ?? 1),
        )
        dayButtonRefs.current[Math.max(selectedCellIndex, 0)]?.focus()
      }
    })

    return () => window.cancelAnimationFrame(animationFrame)
  }, [calendarCells, draft.day, draft.month, draft.year, view, years])

  function changeView(
    nextView: DatePickerCalendarView,
    nextDirection: TransitionDirection,
    shouldMoveFocus = false,
  ) {
    shouldFocusNextView.current = shouldMoveFocus
    setDirection(nextDirection)
    setView(nextView)
    onViewChange?.(nextView)
  }

  function focusGridButton(
    buttons: Array<HTMLButtonElement | null>,
    index: number,
    searchDirection: 1 | -1,
  ) {
    let nextIndex = index

    while (nextIndex >= 0 && nextIndex < buttons.length) {
      const button = buttons[nextIndex]

      if (button && !button.disabled) {
        button.focus()
        return
      }

      nextIndex += searchDirection
    }
  }

  function handleGridKeyDown(
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
    columnCount: number,
    buttons: Array<HTMLButtonElement | null>,
    onSelect: () => void,
  ) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onSelect()
      return
    }

    const rowStart = index - (index % columnCount)
    const rowEnd = Math.min(rowStart + columnCount - 1, buttons.length - 1)
    let nextIndex: number | undefined
    let searchDirection: 1 | -1 = 1

    switch (event.key) {
      case 'ArrowLeft':
        nextIndex = index - 1
        searchDirection = -1
        break
      case 'ArrowRight':
        nextIndex = index + 1
        break
      case 'ArrowUp':
        nextIndex = index - columnCount
        searchDirection = -1
        break
      case 'ArrowDown':
        nextIndex = index + columnCount
        break
      case 'Home':
        nextIndex = rowStart
        break
      case 'End':
        nextIndex = rowEnd
        searchDirection = -1
        break
      default:
        return
    }

    event.preventDefault()
    focusGridButton(buttons, nextIndex, searchDirection)
  }

  function selectYear(year: number, shouldMoveFocus = false) {
    setDraft((currentDraft) => ({
      ...currentDraft,
      year,
      day: currentDraft.month
        ? clampDay(year, currentDraft.month, currentDraft.day)
        : currentDraft.day,
    }))
    onYearSelect?.(year)
    changeView('month', 'forward', shouldMoveFocus)
  }

  function isMonthDisabled(month: number) {
    return Boolean(
      draft.year === today.getFullYear() && month > today.getMonth() + 1,
    )
  }

  function selectMonth(month: number, shouldMoveFocus = false) {
    if (!draft.year || isMonthDisabled(month)) return

    setDraft((currentDraft) => ({
      ...currentDraft,
      month,
      day: clampDay(draft.year!, month, currentDraft.day),
    }))
    onMonthSelect?.(month)
    changeView('day', 'forward', shouldMoveFocus)
  }

  function selectDay(day: number) {
    if (!draft.year || !draft.month) return

    const selectedDate = new Date(draft.year, draft.month - 1, day)
    selectedDate.setHours(0, 0, 0, 0)
    if (selectedDate > today) return

    const nextValue = {
      year: draft.year,
      month: draft.month,
      day,
    }

    setDraft(nextValue)
    onChange?.(nextValue)
  }

  function renderBackHeader(
    label: string,
    path: string,
    previousView: DatePickerCalendarView,
  ) {
    return (
      <header className="flex items-center gap-3 px-0.5">
        <button
          type="button"
          aria-label={`Back to ${previousView} selection`}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#ECECEF] text-[#0B0B0F] transition-[background-color,border-color,color] duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] hover:border-[#D9D9DE] hover:bg-[#F8F8F9] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0B0B0F] focus-visible:ring-offset-2"
          onClick={(event) =>
            changeView(previousView, 'back', event.detail === 0)
          }
        >
          <ChevronLeftIcon className="h-3 w-3" aria-hidden="true" />
        </button>
        <h2
          id={headingId}
          className="text-[14.5px] font-semibold leading-[18px] text-[#0B0B0F]"
        >
          {label}
        </h2>
        <span className="ml-auto text-[13px] font-medium leading-4 text-[#A0A0A8]">
          {path}
        </span>
      </header>
    )
  }

  return (
    <motion.section
      aria-describedby={view === 'year' ? hintId : undefined}
      aria-labelledby={headingId}
      animate={{ height: panelHeight }}
      className={clsx(
        styles.panel,
        'w-[calc(100vw-40px)] max-w-[335px] overflow-hidden rounded-[20px] border border-[#ECECEF] bg-white p-5 font-sans shadow-[0_24px_60px_rgba(11,11,15,0.14)] sm:w-[328px]',
        className,
      )}
      initial={false}
      transition={{
        duration: prefersReducedMotion ? 0 : softMotionDuration.handoff,
        ease: softEditorialEase,
      }}
    >
      <div className={styles.viewStack}>
        <AnimatePresence initial={true} mode="sync" custom={direction}>
          <AnimatedPickerView
            key={view}
            direction={direction}
            reduceMotion={Boolean(prefersReducedMotion)}
          >
            {view === 'year' && (
              <>
                <h2
                  id={headingId}
                  className="text-[13.5px] font-semibold leading-[17px] text-[#0B0B0F]"
                >
                  {title}
                </h2>

                <div className={styles.yearListViewport}>
                  <div
                    ref={yearListRef}
                    role="group"
                    aria-label="Choose a birth year"
                    className={clsx(
                      styles.yearList,
                      'grid h-full grid-cols-4 gap-2 overflow-y-auto overscroll-contain',
                    )}
                  >
                    {years.map((year, index) => {
                      const isSelected = year === draft.year

                      return (
                        <button
                          key={year}
                          ref={(element) => {
                            yearButtonRefs.current[index] = element
                          }}
                          type="button"
                          aria-pressed={isSelected}
                          className={clsx(
                            'flex h-10 items-center justify-center rounded-xl text-[13.5px] leading-[18px] text-[#0B0B0F] transition-[background-color,color] duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0B0B0F] focus-visible:ring-offset-2',
                            isSelected
                              ? 'bg-[#0B0B0F] font-semibold text-white hover:bg-[#24242A]'
                              : 'bg-[#F4F4F6] font-normal hover:bg-[#E8E8EB]',
                          )}
                          onClick={() => selectYear(year)}
                          onKeyDown={(event) =>
                            handleGridKeyDown(
                              event,
                              index,
                              YEAR_COLUMN_COUNT,
                              yearButtonRefs.current,
                              () => selectYear(year, true),
                            )
                          }
                        >
                          {year}
                        </button>
                      )
                    })}
                  </div>
                </div>

                <p
                  id={hintId}
                  className="text-xs leading-[15px] text-[#A0A0A8]"
                >
                  {scrollHint}
                </p>
              </>
            )}

            {view === 'month' && draft.year && (
              <>
                {renderBackHeader('Month', `${draft.year}`, 'year')}

                <div
                  role="group"
                  aria-label={`Choose a birth month in ${draft.year}`}
                  className="grid grid-cols-3 gap-2"
                >
                  {MONTHS.map((monthLabel, index) => {
                    const month = index + 1
                    const isSelected = month === draft.month
                    const isDisabled = isMonthDisabled(month)

                    return (
                      <button
                        key={monthLabel}
                        ref={(element) => {
                          monthButtonRefs.current[index] = element
                        }}
                        type="button"
                        aria-pressed={isSelected}
                        disabled={isDisabled}
                        className={clsx(
                          'flex h-11 items-center justify-center rounded-xl text-[13.5px] leading-[18px] transition-[background-color,color,opacity] duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0B0B0F] focus-visible:ring-offset-2',
                          isSelected
                            ? 'bg-[#0B0B0F] font-semibold text-white hover:bg-[#24242A]'
                            : 'bg-[#F4F4F6] font-normal text-[#0B0B0F] hover:bg-[#E8E8EB]',
                          isDisabled &&
                            'cursor-not-allowed opacity-40 hover:bg-[#F4F4F6]',
                        )}
                        onClick={() => selectMonth(month)}
                        onKeyDown={(event) =>
                          handleGridKeyDown(
                            event,
                            index,
                            MONTH_COLUMN_COUNT,
                            monthButtonRefs.current,
                            () => selectMonth(month, true),
                          )
                        }
                      >
                        {monthLabel}
                      </button>
                    )
                  })}
                </div>
              </>
            )}

            {view === 'day' && draft.year && draft.month && (
              <>
                {renderBackHeader(
                  'Day',
                  `${MONTHS[draft.month - 1]} ${draft.year}`,
                  'month',
                )}

                <div className="flex justify-between px-1" aria-hidden="true">
                  {WEEKDAYS.map((weekday, index) => (
                    <span
                      key={`${weekday}-${index}`}
                      className="w-9 text-center text-[11px] font-semibold leading-[14px] text-[#A0A0A8]"
                    >
                      {weekday}
                    </span>
                  ))}
                </div>

                <div
                  role="grid"
                  aria-label={`${MONTHS[draft.month - 1]} ${draft.year}`}
                  className="grid grid-cols-7 gap-y-1 px-1"
                >
                  {calendarCells.map((cell, index) => {
                    if (!cell.isCurrentMonth) {
                      return (
                        <span
                          key={cell.date.toISOString()}
                          aria-hidden="true"
                          className="flex h-9 w-9 items-center justify-center text-[13.5px] leading-[18px] text-[#C9C9CF]"
                        >
                          {cell.day}
                        </span>
                      )
                    }

                    const cellDate = new Date(
                      draft.year!,
                      draft.month! - 1,
                      cell.day,
                    )
                    cellDate.setHours(0, 0, 0, 0)
                    const isDisabled = cellDate > today
                    const isSelected = cell.day === draft.day

                    return (
                      <button
                        key={cell.date.toISOString()}
                        ref={(element) => {
                          dayButtonRefs.current[index] = element
                        }}
                        type="button"
                        role="gridcell"
                        aria-label={`${cell.day} ${MONTHS[draft.month! - 1]} ${draft.year}`}
                        aria-selected={isSelected}
                        disabled={isDisabled}
                        className={clsx(
                          'flex h-9 w-9 items-center justify-center rounded-full text-[13.5px] leading-[18px] transition-[background-color,color,opacity] duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0B0B0F] focus-visible:ring-offset-1',
                          isSelected
                            ? 'bg-[#0B0B0F] font-semibold text-white hover:bg-[#24242A]'
                            : 'font-normal text-[#0B0B0F] hover:bg-[#F4F4F6]',
                          isDisabled &&
                            'cursor-not-allowed text-[#C9C9CF] hover:bg-transparent',
                        )}
                        onClick={() => selectDay(cell.day)}
                        onKeyDown={(event) =>
                          handleGridKeyDown(
                            event,
                            index,
                            DAY_COLUMN_COUNT,
                            dayButtonRefs.current,
                            () => selectDay(cell.day),
                          )
                        }
                      >
                        {cell.day}
                      </button>
                    )
                  })}
                </div>
              </>
            )}
          </AnimatedPickerView>
        </AnimatePresence>
      </div>
    </motion.section>
  )
}
